import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { first } from 'rxjs/operators/first';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { combineLatest } from 'rxjs/observable/combineLatest';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import {
    ApplicationService,
    AdoptionRequestInfo,
    CollegeCourseInfo,
    CollegeDepartmentInfo,
    CollegeInfo,
    GradRequirementInfo,
    SchoolInfo,
} from '../../services/application/application.service';
import { AuthService } from '../../services/authentication/auth.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { DownloadService, DownloadFormat, HttpMethod } from '../../services/download/download.service';
import { ErrorService } from '../../services/error/error.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserInfo, UserService } from '../../services/user/user.service';

import { SchoolYearPipe } from '../../pipes/schoolyear.pipe';
import { TitleCasePipe } from '../../pipes/titlecase.pipe';
import { Tag, TagTypes } from '../ui/tags/tags.component';

@Component({
    selector: 'app-adoption-request',
    styleUrls: ['adoptionrequest.component.css'],
    templateUrl: 'adoptionrequest.component.html',
})
export class AdoptionRequestComponent implements OnInit, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();

    get ProposedUseID() {
        return this.NewCourseForm && this.NewCourseForm.controls['ProposedUseID'];
    }
    get ProposedUseDescription() {
        return this.NewCourseForm && this.NewCourseForm.controls['ProposedUseDescription'];
    }

    AllHeaders: string[] = ['LastName', 'FirstName', 'SchoolName', 'RoleName', 'CanBypassAdmin', 'DesigneeActions'];
    AvailableColleges: CollegeInfo[];
    AvailableSchools: SchoolInfo[];
    AvailableSchoolYears: string[];
    CollegeCourseID: number;
    CurrentSchoolYear: string;
    FromSearch: boolean;
    GradRequirements: GradRequirementInfo[];
    NewCourseForm: FormGroup;
    PartialViewReady: boolean;
    SelectedCollege: CollegeInfo;
    SmallDisplay: boolean;
    TagOptions: Tag[] | null;
    TagTypes = TagTypes;
    User: UserInfo;

    constructor(
        private appService: ApplicationService,
        private authService: AuthService,
        private decimalPipe: DecimalPipe,
        private dialogService: DialogService,
        private downloadService: DownloadService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private mediaChange: ObservableMedia,
        private route: ActivatedRoute,
        private router: Router,
        private schoolYearPipe: SchoolYearPipe,
        private splashService: SplashService,
        private titleCasePipe: TitleCasePipe,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.AvailableColleges = null;
        self.AvailableSchools = null;
        self.AvailableSchoolYears = null;
        self.CollegeCourseID = null;
        self.CurrentSchoolYear = null;
        self.FromSearch = false;
        self.GradRequirements = null;
        self.NewCourseForm = null;
        self.PartialViewReady = false;
        self.SelectedCollege = null;
        self.SmallDisplay = false;
        self.User = null;

        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
            });

        // Make an empty new user form
        self.NewCourseForm = self.fb.group({
            Code: [null, [Validators.required, Validators.minLength(8), Validators.maxLength(8)]],
            Name: [null, Validators.required],
            GradRequirement: [null, Validators.required],
            ProposedUseID: [null, Validators.required],
            ProposedUseDescription: [null],
            HSCredit: [null, Validators.required],
            ModificationsNeeded: [null, Validators.maxLength(1000)],
            CompanionFieldsNeeded: [null, Validators.maxLength(1000)],
            CompanionFieldsReason: [null, Validators.maxLength(2000)],
            SchoolYear: [null, Validators.required],
            School: [null, Validators.required],
            CollegeIDs: [[] as number[], [Validators.min(1)]],
        });

        // Initially disable this control.
        self.NewCourseForm.controls['ProposedUseDescription'].disable();

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            // Load static data for use in the compoment.
            const collegeObs = self.appService.GetAllColleges(self.User.AuthToken);
            const curYearObs = self.appService.GetCurrentSchoolYear();
            const gradReqObs = self.appService.GetAllGradRequirements();
            const schoolsObs = self.appService.GetAllSchools(self.User.AuthToken);
            const schoolYearsObs = self.appService.GetAllSchoolYearsForAdoptions();

            combineLatest(collegeObs, curYearObs, gradReqObs, schoolsObs, schoolYearsObs, self.route.params, function(
                colleges,
                curyear,
                gradreqs,
                schools,
                years,
                params
            ) {
                return {
                    Colleges: colleges,
                    CurrentYear: curyear,
                    GradReqs: gradreqs,
                    Schools: schools,
                    Years: years,
                    Params: params,
                };
            })
                .pipe(takeUntil(self._subrelease))
                .subscribe(
                    packet => {
                        self.AvailableColleges = packet.Colleges;
                        self.AvailableSchools = packet.Schools;
                        self.AvailableSchoolYears = packet.Years;
                        self.CurrentSchoolYear = packet.CurrentYear;
                        self.GradRequirements = packet.GradReqs;
                        self.CollegeCourseID =
                            packet.Params && packet.Params['CollegeCourseID'] != null ? +packet.Params['CollegeCourseID'] : null;

                        // If the current user has an identified school (not 0000/9000), set that as the current school in the form.
                        if (self.User.SchoolNumber && self.User.SchoolNumber !== '0000' && self.User.SchoolNumber !== '9000') {
                            const sch = self.AvailableSchools.find(item => item.SchoolNumber === self.User.SchoolNumber);
                            if (sch) {
                                self.NewCourseForm.controls['School'].setValue(sch, { emitEvent: false });
                            }
                        }

                        // Subscribe to changes in the "ProposedUseID" form value; when it goes to 1, we need to enable the
                        // "Other" field and make it required.
                        self.ProposedUseID.valueChanges.pipe(takeUntil(self._subrelease)).subscribe(val => {
                            if (val === 0) {
                                self.ProposedUseDescription.clearValidators();
                                self.ProposedUseDescription.disable();
                            } else {
                                self.ProposedUseDescription.setValidators(Validators.required);
                                self.ProposedUseDescription.enable();
                            }

                            self.ProposedUseDescription.updateValueAndValidity({ emitEvent: false });
                        });

                        // Pre-select Polk State for now.
                        if (self.AvailableColleges && self.AvailableColleges.length) {
                            self.SelectedCollege = self.AvailableColleges.find(item => item.CollegeAbbreviation === 'Polk State');
                            self.TagOptions = self.AvailableColleges.map((c: CollegeInfo) => new Tag(c.CollegeName, c.CollegeID, c.Active));
                        }

                        // Do we have a params value? If so, we're coming here from a redirect, and try to load the course\
                        // info for the course ID given so we can fill in the basic fields.
                        if (self.CollegeCourseID != null) {
                            // Get the course info
                            self.appService.GetCollegeCourseInfoByID(self.User.AuthToken, self.CollegeCourseID).subscribe(
                                cinfo => {
                                    const patch = {
                                        // Prefix : cinfo.Prefix,
                                        // Code : cinfo.Code,
                                        Name: cinfo.Name, // Only pre-filling the name field for now until we figure out the request design
                                    };

                                    self.NewCourseForm.patchValue(patch);

                                    const input = <HTMLInputElement>document.querySelector('.codeinput');
                                    input.focus();

                                    self.PartialViewReady = true;
                                },
                                error => {
                                    self.errorService.HandleServiceError(error);
                                }
                            );
                        } else {
                            const input = <HTMLInputElement>document.querySelector('.codeinput');
                            input.focus();

                            // Ready for component use.
                            self.PartialViewReady = true;
                        }
                    },
                    error => {
                        self.errorService.HandleServiceError(error);
                    }
                );
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    // #region Add New Request Method

    AddNewRequest(chipCtrl: FormControl) {
        const self = this;
        const colleges = chipCtrl.value as Tag[]

        // Patch value from child component before continueing
        if (self.NewCourseForm.get('CollegeIDs'))
        {
            self.NewCourseForm.get('CollegeIDs')!.patchValue(colleges.map(x => x.id));
        }

        const form = <NewCourseInfo>self.NewCourseForm.getRawValue();
        const nColleges = (<number[]>form.CollegeIDs);
        const collegeNames = self.AvailableColleges.filter(x => nColleges.includes(x.CollegeID)).map(x => x.CollegeName).join(', ');

        // Get the user confirmation before proceeding.
        const message =
            `You are about to submit the following new <strong><em>Dual Enrollment</em></strong> ` +
            `course adoption request:<br /><br /><strong>Course Name/Title: </strong>${form.Name}<br />` +
            `<strong>Course Code: </strong>${form.Code}<br /><strong>Program: </strong>${form.GradRequirement.Name}<br />` +
            `<strong>High School Credits: </strong>${self.decimalPipe.transform(form.HSCredit, '1.1-1')}<br />` +
            `<strong>School Year: </strong>${self.schoolYearPipe.transform(form.SchoolYear)}<br />` +
            `<strong>School: </strong>${form.School.SchoolName}<br />` +
            `<strong>College(s): </strong>${collegeNames}<br /><br />` +
            `Are you sure you wish to submit the course adoption request as summarized above?`;

        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: message,
                OKText: `Yes - Submit DE Request`,
                CancelText: 'No - CANCEL',
            })
            .subscribe(success => {
                if (success === true) {
                    // Submit adoption request
                    form.SchoolNumber = form.School.SchoolNumber;
                    form.GradRequirementID = form.GradRequirement.GradRequirementID;

                    self.splashService
                        .DisplaySplash({ TitleText: 'Please Wait', MessageText: 'Submitting request...' })
                        .subscribe(() => {
                            self.appService.AddAdoptionRequest(self.User.AuthToken, self.User.CurrentRole, form, form.CollegeIDs).subscribe(
                                adoptID => {
                                    self.splashService.CloseSplash();

                                    self.splashService.SnackbarSuccess('Course Adoption Request Submitted Successfully.');

                                    // Clear the form.
                                    self.ResetNewCourseForm(chipCtrl);
                                },
                                error => {
                                    self.errorService.HandleServiceError(error);
                                }
                            );
                        });
                }
            });
    }

    // #endregion

    // #region Reset New Course Form Method

    ResetNewCourseForm(chipCtrl: FormControl) {
        // Need to pass in control value from child component to manually reset it to remove the chip list when resetting the form.
        if (chipCtrl){
            chipCtrl.reset();
        }

        this.NewCourseForm.reset();

        const input = <HTMLInputElement>document.querySelector('.codeinput');
        input.focus();

        if (this.CollegeCourseID != null) {
            this.router.navigate(['/adoptionrequest']);
        }
    }

    // #endregion

    // #region Show Course Search Method

    ShowCourseSearch() {
        const self = this;

        // Pop up a course search dialog.
        self.dialogService
            .OpenAdoptionCourseSearchDialog({
                TitleText: 'Select course...',
                MessageText:
                    `Use the selection boxes below to choose a course department and specific course to request. ` +
                    `You may also do a free-text search by using the provided space below, which will look for any courses ` +
                    `which have the provided text within their course name or course description. Once a course is selected, ` +
                    `the information for the course will be displayed for your review. To select the course for the adoption ` +
                    `request form, click the "Select Course" button. If you wish to exit without selecting a course, click ` +
                    `the "Cancel" button.`,
                Colleges: self.AvailableColleges,
                CurrentSchoolYear: self.CurrentSchoolYear,
                SelectedCollege: self.SelectedCollege,
                OKText: 'Select Course',
                CancelText: 'Cancel',
            })
            .subscribe(coursechosen => {
                if (!coursechosen) {
                    return;
                }

                // Copy over the details as needed into the new course form.
                const patch = {
                    // Prefix : coursechosen.Prefix,
                    // Code : coursechosen.Code,
                    Name: coursechosen.Name,
                };

                self.NewCourseForm.patchValue(patch);
            });
    }

    // #endregion
}

// #region Custom Classes

// #region New Course Info Class

export class NewCourseInfo extends AdoptionRequestInfo {
    GradRequirement?: GradRequirementInfo;
    School?: SchoolInfo;
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/app/adoptionrequest/adoptionrequest.component.ts